import { map } from 'lodash-es'
import { FC, useEffect, useRef, useState } from 'react'
import { GripVertical, Search, X } from 'lucide-react'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { TableSearchParams } from '../../home/components/tickets/generate-tickets/GenerateTicketsListPage'
import { Button } from '@/components/ui/button'
import { InputGroup } from '@/components/ui/input-group'
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

export interface IColumn {
  title: string | JSX.Element
  accessor: string
  sortable?: boolean
  width?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (row: any, index: number) => JSX.Element | null | string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: (row: any) => React.CSSProperties
  searchable?: boolean
}

interface ITable extends React.HTMLAttributes<HTMLTableElement> {
  columns: IColumn[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]
  isCheck?: string[]
  handleSortBy?: (column: string) => void
  columnWidth?: string
  handleSearch?: (column: IColumn, query: string | null) => void
  loading?: boolean
  noDataMessage?: string | JSX.Element
  searchParams?: TableSearchParams
  alignment?: string
  /**
   * Enables drag-to-reorder rows (row sorting).
   * When enabled, the table renders a small drag handle column.
   */
  rowReorder?: boolean
  /**
   * Extract a stable row id used for React keys and drag/drop.
   * Defaults to `row.id` (if present) otherwise falls back to index.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowId?: (row: any, index: number) => string
  /**
   * Called after a drop completes with the reordered array (same items, new order).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowReorder?: (nextData: any[]) => void
  /**
   * Max height for the scroll container around the table.
   * Defaults to a viewport-based value to keep pagination visible.
   */
  maxHeight?: string
}

const NoDataComponent = ({
  message,
  colSpan,
}: {
  message?: string | JSX.Element
  colSpan?: number
}) => (
  <TableRow className="border-0">
    <TableCell
      colSpan={colSpan}
      className="py-10 text-center text-muted-foreground"
    >
      {message || 'There is no data'}
    </TableCell>
  </TableRow>
)

const ThElement = ({
  column,
  columnWidth,
  handleSortBy,
  handleSearch,
  query: queryProp,
  columnIndex,
}: {
  column: IColumn
  columnWidth?: string
  handleSortBy?: (column: string) => void
  handleSearch?: (column: IColumn, query: string | null) => void
  query?: string | null
  columnIndex: number
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState(false)
  const [query, setQuery] = useState<string | null>('')
  const onChange = (val: string | null) => {
    setQuery(val)
    handleSearch?.(column, val)
  }

  useEffect(() => {
    if (queryProp) {
      setQuery(queryProp)
      setSearch(true)
    } else {
      setQuery(null)
      setSearch(false)
    }
  }, [queryProp])

  const isSortable = Boolean(column.accessor && column.sortable)

  return (
    <TableHead
      className={cn(
        'h-12 px-4 text-left align-middle font-medium text-monochrome-2',
        'first:rounded-l-[10px] last:rounded-r-[10px]',
        'bg-monochrome-4 backdrop-blur-[21px]',
        isSortable && 'cursor-pointer nowrap',
      )}
    >
      {!search ? (
        <div
          className="flex h-[31px] w-full items-center justify-between gap-2"
          style={{ height: 31 }}
          onClick={() => (isSortable ? handleSortBy?.(column.accessor) : null)}
        >
          <span className="flex-grow-1">{column.title}</span>
          {column.searchable && (
            <Button
              size="icon-sm"
              variant="ghost"
              className="h-8 w-8 rounded-full text-monochrome-2 hover:bg-monochrome-5/70"
              onClick={(e) => {
                e.stopPropagation()
                setSearch(true)
                setTimeout(() => {
                  ref.current?.focus()
                })
              }}
            >
              <Search className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <div
          className="flex w-full items-center justify-between"
          style={{
            minWidth: '200px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <InputGroup
            ref={ref}
            type="text"
            value={query || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Search"
            className="h-9 bg-monochrome-3-60 text-monochrome-1 placeholder:text-monochrome-2/80"
            suffix={
              <button
                type="button"
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(null)
                  setSearch(false)
                }}
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            }
            suffixClassName="bg-monochrome-3-60 text-monochrome-2 hover:text-monochrome-1"
          />
        </div>
      )}
    </TableHead>
  )
}

const SortableBodyRow = ({
  rowId,
  row,
  index,
  columns,
  alignment,
  isSelected,
}: {
  rowId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any
  index: number
  columns: IColumn[]
  alignment: string
  isSelected: boolean
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        // ensure hover works even though cells have background
        'group [&_td]:px-4 [&_td]:py-4 [&_td]:border-b [&_td]:border-monochrome-120 [&_td]:group-hover:bg-monochrome-3-60',
        isDragging && 'relative z-10 opacity-60 [&_td]:bg-white/5',
        isSelected &&
          '[&_td]:bg-monochrome-3-60 [&_td:first-child]:rounded-l-[20px] [&_td:last-child]:rounded-r-[20px]',
      )}
      data-state={isSelected ? 'selected' : undefined}
    >
      <TableCell className="px-2 py-4 align-middle">
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label="Drag to reorder row"
          className={cn(
            'flex h-9 w-9 select-none items-center justify-center rounded-full',
            'text-monochrome-2',
            'cursor-grab active:cursor-grabbing',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-1/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
          )}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>

      {map(columns, (column, tdIndex) => (
        <TableCell
          key={`td-${rowId}-${tdIndex}`}
          style={{
            verticalAlign: alignment as React.CSSProperties['verticalAlign'],
            ...column.style?.(row),
          }}
        >
          {column.render ? column.render(row, index) : row[column.accessor]}
        </TableCell>
      ))}
    </TableRow>
  )
}

export const Table: FC<ITable> = ({
  columns,
  data,
  isCheck,
  handleSortBy,
  columnWidth,
  handleSearch,
  loading,
  noDataMessage,
  searchParams,
  alignment = 'top',
  rowReorder = false,
  getRowId,
  onRowReorder,
  maxHeight = 'calc(100svh - 280px)',
  className,
  style,
  ...props
}) => {
  const autoRowIdMapRef = useRef<WeakMap<object, string>>(new WeakMap())
  const autoRowIdCounterRef = useRef(0)

  const resolvedGetRowId = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    row: any,
    index: number,
  ) => {
    if (getRowId) return getRowId(row, index)
    if (row?.id != null) return String(row.id)

    if (row && typeof row === 'object') {
      const existing = autoRowIdMapRef.current.get(row)
      if (existing) return existing
      autoRowIdCounterRef.current += 1
      const next = `row-${autoRowIdCounterRef.current}`
      autoRowIdMapRef.current.set(row, next)
      return next
    }

    return String(index)
  }

  // Keep an internal list for immediate UI updates while reordering.
  const [displayData, setDisplayData] = useState(data)

  useEffect(() => {
    if (!rowReorder) return
    // When parent changes data (pagination/filter/sort), reflect it.
    setDisplayData(data)
  }, [data, rowReorder])

  const activeData = rowReorder ? displayData : data
  const sortableIds = rowReorder
    ? activeData.map((row, index) => resolvedGetRowId(row, index))
    : []

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!rowReorder) return
    if (!over) return

    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    const oldIndex = sortableIds.indexOf(activeId)
    const newIndex = sortableIds.indexOf(overId)
    if (oldIndex < 0 || newIndex < 0) return

    const next = arrayMove(activeData, oldIndex, newIndex)
    setDisplayData(next)
    onRowReorder?.(next)
  }

  const tableContent = (
    <ShadcnTable
      className={cn(
        // "floating rows" look
        'border-separate border-spacing-y-0',
        // keep the header visually distinct
        'text-monochrome-1',
        className,
      )}
      containerClassName={cn(
        'overflow-auto',
        // keep some right padding so scrollbars don't hug content
        'pr-2',
      )}
      containerStyle={{ maxHeight }}
      style={style}
      {...props}
    >
      <TableHeader className="[&_tr]:border-0">
        <TableRow className="border-0 sticky top-0 z-50">
          {rowReorder && (
            <TableHead
              style={{ width: 44 }}
              className={cn(
                'h-12 px-2 text-left align-middle font-medium text-monochrome-2',
                'first:rounded-l-[10px]',
                'bg-monochrome-4 backdrop-blur-[21px]',
              )}
            />
          )}
          {map(columns, (column, index) => (
            <ThElement
              columnWidth={columnWidth}
              key={`head-${index}`}
              handleSortBy={handleSortBy}
              column={column}
              handleSearch={handleSearch}
              query={searchParams?.[column.accessor]}
              columnIndex={index}
            />
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rowReorder ? (
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            {map(activeData, (row, index) => {
              const rowId = resolvedGetRowId(row, index)
              const isSelected = Boolean(isCheck?.includes(row.id))
              return (
                <SortableBodyRow
                  key={`body-row-${rowId}`}
                  rowId={rowId}
                  row={row}
                  index={index}
                  columns={columns}
                  alignment={alignment}
                  isSelected={isSelected}
                />
              )
            })}
          </SortableContext>
        ) : (
          map(activeData, (row, index) => {
            const isSelected = Boolean(isCheck?.includes(row.id))
            const rowId = resolvedGetRowId(row, index)
            return (
              <TableRow
                key={`body-row-${rowId}`}
                className={cn(
                  // ensure hover works even though cells have background
                  'group [&_td]:px-4 [&_td]:py-4 [&_td]:border-b [&_td]:border-monochrome-120',
                  isSelected &&
                    '[&_td]:bg-monochrome-3-60 [&_td:first-child]:rounded-l-[20px] [&_td:last-child]:rounded-r-[20px]',
                )}
                data-state={isSelected ? 'selected' : undefined}
              >
                {map(columns, (column, tdIndex) => (
                  <TableCell
                    key={`td-${rowId}-${tdIndex}`}
                    style={{
                      verticalAlign:
                        alignment as React.CSSProperties['verticalAlign'],
                      ...column.style?.(row),
                    }}
                    className={cn(
                      // floating rounded row
                      'group-hover:bg-white/5',
                      // ensure a visible row divider with border-separate
                      'border-b border-monochrome-120',
                      // outline around the "row pill"
                      'px-4 py-4',
                    )}
                  >
                    {column.render
                      ? column.render(row, index)
                      : row[column.accessor]}
                  </TableCell>
                ))}
              </TableRow>
            )
          })
        )}

        {activeData.length === 0 && !loading && (
          <NoDataComponent
            message={noDataMessage}
            colSpan={columns.length + (rowReorder ? 1 : 0)}
          />
        )}
      </TableBody>
    </ShadcnTable>
  )

  if (!rowReorder) {
    return tableContent
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
    >
      {tableContent}
    </DndContext>
  )
}
